import * as ko from "knockout";
import {moment} from 'moment';
import pubsub from "../../../pubsub";
import {ErrorTracker} from "../../../error.tracker";
import {IObjectData, IOverride, ISystem, ISystemPermission, ISystemRole} from "../../../index";
import {ISubmitDataOverride, TSystemPermissionStatus} from "./index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {Organization} from "./Organization";
import {INPUT_DATE_FORMAT, SYSTEM_MEGA_EADM} from "../../../constants";
import {convertFromServerString, convertToServerString} from "../../../util/datetime";
import {getEadmRole, getNextId, getPermissionDisplayName} from "../../../util/misc";
import {global} from "../../../appContext";
import {_} from 'lodash';
import {TComponentName} from "../../../constants/ComponentRegistry";

export abstract class AbstractSystemPermission {
    readonly expiryDate: KnockoutObservable<string | null> = ko.observable(null);
    readonly managerOf: KnockoutObservable<number | null> = ko.observable(null);
    readonly recursive: KnockoutObservable<boolean | null> = ko.observable(null);
    readonly role: KnockoutObservable<ISystemRole | null> = ko.observable(null);
    readonly system: KnockoutObservable<ISystem | null> = ko.observable(null);
    readonly comment: KnockoutObservable<string | null> = ko.observable(null);

    protected abstract readonly isOverrideable: boolean;
    protected id: number = 0;
    protected readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    protected readonly resetRoleEventName = 'reset.role.' + getNextId();
    protected templateName!: TComponentName;
    readonly isDeleted: KnockoutObservable<boolean | null> = ko.observable(null);
    readonly manual: boolean;
    readonly override: KnockoutObservable<IOverride | null> = ko.observable(null);
    readonly toggleOpenComment: KnockoutObservable<boolean | null> = ko.observable(null)

    constructor(public org: Organization, protected user: IObjectData | null, protected sp: ISystemPermission) {
        this.manual = sp.manual;
        this.kosub.subscribe(this.role, (role: ISystemRole | null) => {
            if (!role) {
                this.recursive(false);
                this.managerOf(0);
                return;
            }

            const recursive = role.recursive;
            this.recursive(recursive);
            if (!recursive) {
                this.managerOf(0);
                return;
            }

            const managerOf = this.managerOf();
            if (!managerOf) {
                this.managerOf(this.org.departmentId);
            }
        });
    }

    /*******************************************************************************************************************
     * Data initialization, validation and check
     ******************************************************************************************************************/

    protected init(): void {
        this.templateName = this.isMutable() ? 'component/permission/system-permission/mutable' : 'component/permission/system-permission/immutable';
        this.id = this.sp.id;
    }

    isChanged(): boolean {
        return this.getStatus() != 'unchanged';
    }


    isValid(errorTracker: ErrorTracker): boolean {
        const a = !!this.system();

        // ask "the parent" whether multiple instances of same system/role is valid
        const b = !this.org.isMultipleRoleSameCompanyValid(this);
        // ask "the parent" whether multiple instances are equal
        const f = !this.org.isMultipleEqual(this);
        if (!a) {
            errorTracker.update(true, this.system, `invalid collection`);
        } else if (!b) {
            const system = this.system();
            errorTracker.update(true, this.system, `((only one permission per collection)) [${system?.name ?? ''}]`);
        } else if (!f) {
            errorTracker.update(true, this.system, `permission already assigned`);
        } else {
            errorTracker.update(false, this.system);
        }

        const c = !!this.role();
        errorTracker.update(!c, this.role, "invalid permission");

        // ask "the parent" whether multiple instances of same departments are valid
        const d = (() => {
            const system = this.system();
            // let's not bother if system is not set or if multiple roles in same department is valid
            if (!system || system.multiplerolesamedepartment) {
                return true;
            }
            // also let's not bother if role or managerOf is not set
            if (!this.role() || !this.managerOf()) {
                return true;
            }
            return !this.org.isMultipleRoleSameDepartmentValid(this);
        })();

        const e = (() => {
            const role = this.role();
            // let's not bother if role is not set
            if (!role) {
                return true;
            }
            // also let's not bother if managerOf is not mandatory
            if (!role.recursive) {
                return true;
            }
            return !!this.managerOf();
        })();

        if (!d) {
            errorTracker.update(true, this.managerOf, `department assigned more than once`);
        } else if (!e) {
            errorTracker.update(true, this.managerOf, `department not assigned`);
        } else {
            errorTracker.update(false, this.managerOf);
        }

        return a && b && c && d && e && f;
    }

    /*******************************************************************************************************************
     * Data initialization, validation and check
     ******************************************************************************************************************/

    protected initData() {
        this.system(this.sp.system);
        this.role(this.sp.role);
        this.managerOf(this.sp.managerOf);
        this.expiryDate(this.sp.expirydate ? convertFromServerString(this.sp.expirydate).format(INPUT_DATE_FORMAT) : null);
        this.recursive(this.sp.recursive);
        this.comment(this.sp.comment);
        pubsub.trigger(this.resetRoleEventName, this.sp.role?.id);
    }

    protected isPropertiesChanged(): boolean {
        const _n = this.expiryDate();
        const oldDate = this.sp.expirydate;
        const newDate = _n ? moment(_n).format('M/D/YYYY hh:mm:ss A') : '';
        const a = this.sp.companyid != this.org.org.organizationid;
        const b = oldDate != newDate;
        const c = this.sp.managerOf != this.managerOf();
        const d = this.sp.recursive != this.recursive();
        const e = this.sp.role?.id != this.role()?.id;
        const f = this.sp.system?.id != this.system()?.id;
        const g = this.sp.comment != this.comment();
        return a || b || c || c || d || e || f || g;
    }

    /*******************************************************************************************************************
     * View helpers
     ******************************************************************************************************************/

    private getTitle(manual: boolean): string {
        return manual ? 'Tilgangen er gitt manuelt og ikke som en del av synkronisering.' : 'Tilgangen er gitt automatisk som del av synkronisering';
    }

    public getPermissionDisplayName = (): string => {
        const role = this.role();
        const system = this.system();
        return role && system ? (getPermissionDisplayName(role, system) ?? '') : '';
    }

    /*******************************************************************************************************************
     * Miscellaneous
     ******************************************************************************************************************/

    protected isMutable(): boolean {
        if (this.system()?.definitionreadonly) {
            return false;
        }

        if (this.system()?.name == SYSTEM_MEGA_EADM) {
            const eadmRole = getEadmRole(global.login.getOrganizationById(this.sp.companyid));
            const userRole = this.sp.role?.roleenum;
            if (eadmRole && userRole) {
                return eadmRole >= userRole;
            }
        }

        return this.manual || this.isOverrideable;
    }

    /*******************************************************************************************************************
     * Miscellaneous
     ******************************************************************************************************************/

    dispose() {
        this.kosub.unsuscribeAll();
    }

    abstract containsNewOverrides(): boolean;

    abstract delete(): void;

    abstract getStatus(): TSystemPermissionStatus;

    abstract getSubmitDataOverride(): ISubmitDataOverride | null;

    abstract restore(): void;

    protected showRestoreButton(): boolean {
        return ['deleted', 'changed'].indexOf(this.getStatus()) != -1;
    }

    protected showDeleteButton(): boolean {
        return !this.showRestoreButton();
    }

    getSubmitDataPermission(): ISystemPermission[] {
        const ret: ISystemPermission[] = [];
        switch(this.getStatus()) {
            case 'changed':
            case 'new':
                ret.push(this.getNewSystemPermission());
                break;
            case 'unchanged':
                ret.push(this.sp);
        }
        return ret;
    }

    protected getNewSystemPermission(): ISystemPermission {
        return _.defaults({
            comment: this.comment(),
            expirydate: this.expiryDate() ? convertToServerString(moment(this.expiryDate(), INPUT_DATE_FORMAT)) : '',
            id: 0,
            managerOf: this.managerOf(),
            manual: true,
            recursive: this.recursive(),
            role: this.role(),
            system: this.system(),
        }, this.sp);
    }

}
